import {
  Mutation,
  Action,
  VuexModule,
  getModule,
  Module
} from 'vuex-module-decorators'
import store from '@/store/store'
import {
  SapLoginInfo,
  verifyLogin,
  verifyLoginAccesshtml,
  ErrorModalData
} from '../../modules/util/SapUtil'
import RegisteredUserInfo from '@/modules/class/RegisteredUserInfo'
import UnregisterInfo from '@/modules/class/UnregisterInfo'
import ErrorFormResponse from '../../modules/class/ErrorFormResponse'

export type PageStepName = 'input' | 'confirmation' | 'complete'
/**
 * DL用ステータス
 * 0:認証前 | 1:DL、OK | 2:権限不足 | 3:未ログイン | 4:再DLトライ | 5:再DL待ち
 */
type DlStatus = 0 | 1 | 2 | 3 | 4 | 5

/**
 * ブックマーク追加用ステータス
 * 0:認証前 | 1:認証開始 | 2:追加処理開始
 */
type BookmarkStatus = 0 | 1 | 2

/**
 * ブックマーク処理区分
 * 0:none | 1:ページ | 2:製品カード
 */
type BookmarkDivision = 0 | 1 | 2

export interface MembershipStateI {
  isRegisterModalVisible: boolean
}
@Module({ dynamic: true, store, name: 'membership-state', namespaced: true })
class MembershipState extends VuexModule implements MembershipStateI {
  // -------------------------------------------------------------------------------------------------------------------
  // state
  // ⚡Stateでコンポーネント間における共通の状態を管理します。Stateはコンポーネントから利用されます。⚡
  public _isRegisterModalVisible = false
  get isRegisterModalVisible(): boolean {
    return this._isRegisterModalVisible
  }

  public _isRegisterAdditionalModalVisible = false
  get isRegisterAdditionalModalVisible(): boolean {
    return this._isRegisterAdditionalModalVisible
  }

  public _isRegisterModalAddCompleteVisible = false
  get isRegisterModalAddCompleteVisible(): boolean {
    return this._isRegisterModalAddCompleteVisible
  }

  public _level = 1
  get level(): number {
    return this._level
  }

  public _isLogin = false
  get isLogin() {
    return this._isLogin
  }

  public _isLoginModalVisible = false
  get isLoginModalVisible(): boolean {
    return this._isLoginModalVisible
  }

  public _isRenewalUserModalVisible = false
  get isRenewalUserModalVisible(): boolean {
    return this._isRenewalUserModalVisible
  }

  public _isPasswordReminderModalVisible = false
  get isPasswordReminderModalVisible(): boolean {
    return this._isPasswordReminderModalVisible
  }

  public _userInfo: RegisteredUserInfo = {
    result: false,
    email: '',
    firstname: '',
    lastname: '',
    firstKanaName: '',
    lastKanaName: '',
    organization: '',
    jobRoles: '',
    phone: '',
    mobilePhone: '',
    countryRegion: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    department: '',
    industry: '',
    emailSubscription: false,
    productCategory: [],
    field: [],
    updateDttm: ''
  }

  public _unregisterInfo: UnregisterInfo = {
    resultError: {},
    isError: false
  }

  get userInfo(): RegisteredUserInfo {
    return this._userInfo
  }

  public _currentChangeMembershipStep: PageStepName = 'input'
  get currentChangeMembershipStep(): PageStepName {
    return this._currentChangeMembershipStep
  }

  // POST用のPDF DL用URL情報を保持する。
  public _pdfDownloadAccessUrl = ''
  get pdfDownloadAccessUrl(): string {
    return this._pdfDownloadAccessUrl
  }

  public _pdfDownloadStatus: DlStatus = 0
  get pdfDownloadStatus(): number {
    return this._pdfDownloadStatus
  }

  public _waitingForAddBookmark: BookmarkStatus = 0
  get waitingForAddBookmark(): BookmarkStatus {
    return this._waitingForAddBookmark
  }

  public _isBookmarked = false
  get isBookmarked(): boolean {
    return this._isBookmarked
  }

  public _isNonLoginAlertModalVisible = false
  get isNonLoginAlertModalVisible(): boolean {
    return this._isNonLoginAlertModalVisible
  }

  public _linkUrlPendingRegistration = ''
  get linkUrlPendingRegistration(): string {
    return this._linkUrlPendingRegistration
  }

  public _linkUrlTitlePendingRegistration = ''
  get linkUrlTitlePendingRegistration(): string {
    return this._linkUrlTitlePendingRegistration
  }

  public _bookmarkDivision: BookmarkDivision = 0
  get bookmarkDivision(): BookmarkDivision {
    return this._bookmarkDivision
  }

  private _sapLoginInfo: SapLoginInfo = {
    UID: '',
    UIDSignature: '',
    signatureTimestamp: ''
  }

  public get sapLoginInfo(): SapLoginInfo {
    return this._sapLoginInfo
  }

  public get unregisterInfo(): UnregisterInfo {
    return this._unregisterInfo
  }

  // access.htmlからリダイレクトするため用
  public _redirectUrl = ''
  get redirectUrl(): string {
    return this._redirectUrl
  }

  // sso ログイン中判定用
  private _isLoadingLogin = false
  get isLoadingLogin(): boolean {
    return this._isLoadingLogin
  }

  // エラーメッセージ
  private _errorData = {
    errorFlg: false,
    aipErrorMessage: new Array<string>(),
    propertiesErrorMessage: ''
  }

  public get errorModalData() {
    return this._errorData
  }

  // ------------------------------------------------------------------------------------------------
  // mutations
  // ⚡MutationsでStateの値を更新します。⚡
  @Mutation
  public addIsRegisterModalVisible(b: boolean) {
    this._isRegisterModalVisible = b
  }

  @Mutation
  public addIsRegisterAdditionalModalVisible(b: boolean) {
    this._isRegisterAdditionalModalVisible = b
  }

  @Mutation
  public addIsRegisterModalAddCompleteVisible(b: boolean) {
    this._isRegisterModalAddCompleteVisible = b
  }

  @Mutation
  public addLevel(s: number) {
    this._level = s
  }

  @Mutation
  public addIsLogin(b: boolean) {
    this._isLogin = b
  }

  @Mutation
  public addIsLoginModalVisible(b: boolean) {
    this._isLoginModalVisible = b
  }

  @Mutation
  public addIsRenewalUserModalVisible(b: boolean) {
    this._isRenewalUserModalVisible = b
  }

  @Mutation
  public addIsPasswordReminderModalVisible(b: boolean) {
    this._isPasswordReminderModalVisible = b
  }

  @Mutation
  public addUserInfo(u: RegisteredUserInfo) {
    this._userInfo = u
  }

  @Mutation
  public addCurrentChangeMembershipStep(s: PageStepName) {
    this._currentChangeMembershipStep = s
  }

  @Mutation
  public addAccessUrl(u: string) {
    this._pdfDownloadAccessUrl = u
  }

  @Mutation
  public addDownloadStatus(u: DlStatus) {
    this._pdfDownloadStatus = u
  }

  @Mutation
  public addWaitingForAddBookmark(bs: BookmarkStatus) {
    this._waitingForAddBookmark = bs
  }

  @Mutation
  public addIsBookmarked(b: boolean) {
    this._isBookmarked = b
  }

  @Mutation
  public addIsNonLoginAlertModalVisible(b: boolean) {
    this._isNonLoginAlertModalVisible = b
  }

  @Mutation
  public addLinkUrlPendingRegistration(s: string) {
    this._linkUrlPendingRegistration = s
  }

  @Mutation
  public addLinkUrlTitlePendingRegistration(s: string) {
    this._linkUrlTitlePendingRegistration = s
  }

  @Mutation
  public addBookmarkDivision(bd: BookmarkDivision) {
    this._bookmarkDivision = bd
  }

  @Mutation
  public addSapLoginInfo(sli: SapLoginInfo) {
    this._sapLoginInfo = sli
  }

  @Mutation
  public addUnregisterisError(isError: boolean) {
    this._unregisterInfo.isError = isError
  }

  @Mutation
  public addUnregisterResultError(resultError: ErrorFormResponse) {
    this._unregisterInfo.resultError = resultError
  }

  @Mutation
  public addRedirectUrl(url: string) {
    this._redirectUrl = url
  }

  @Mutation
  public addIsLoadingLogin(b: boolean) {
    this._isLoadingLogin = b
  }

  @Mutation
  public addErrorModalData(data: ErrorModalData) {
    this._errorData.errorFlg = data.errorFlg
    this._errorData.aipErrorMessage =
      data.aipErrorMessage ?? new Array<string>()
    this._errorData.propertiesErrorMessage = data.propertiesErrorMessage ?? ''
  }

  // ------------------------------------------------------------------------------------------------
  // action
  // ⚡ActionでAPI等の外部通信を行う処理や、コンポーネントから呼び出された時の処理を行い、Mutationを利用します。⚡
  @Action({ rawError: true })
  public changeRegisterVisible(b: boolean) {
    this.addIsRegisterModalVisible(b)
  }

  @Action({ rawError: true })
  public changeNonLoginAlertModalVisible(b: boolean) {
    this.addIsNonLoginAlertModalVisible(b)
  }

  // 認証
  @Action({ rawError: true })
  public async checkLogin() {
    verifyLogin()
  }

  // 認証(access.html用)
  @Action({ rawError: true })
  public async checkLoginAccessHtml() {
    verifyLoginAccesshtml()
  }
}

export const membershipState = getModule(MembershipState)
